import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, TrendingUp, Sparkles, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/landing/Eyebrow";
import { CountUp } from "@/components/landing/Motion";
import { CASES, WEBINAR, SCROLL_REVEAL } from "@/lib/content";

// Growth bar (used in hero case)
const GrowthBar = ({ metricLabel, beforeValue, afterValue, beforeLabel, afterLabel, size = "md" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
  const max = Math.max(afterValue, 1);
  const beforePct = Math.max(2, Math.min(100, (beforeValue / max) * 100));
  const barH = size === "lg" ? "h-2" : "h-1.5";

  return (
    <div ref={ref} className="space-y-3">
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
        <span>{metricLabel}</span>
        <span className="font-num text-[hsl(var(--muted-foreground))]/70">BEFORE → AFTER</span>
      </div>
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-[11px] tracking-wider text-[hsl(var(--muted-foreground))]">BEFORE</span>
          <span className="font-num text-[13px] text-[hsl(var(--muted-foreground))]">{beforeLabel}</span>
        </div>
        <div className={`${barH} rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden`}>
          <motion.div initial={{ width: 0 }} animate={{ width: inView ? `${beforePct}%` : 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full bg-[rgba(255,255,255,0.2)]" />
        </div>
      </div>
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-[11px] tracking-wider text-[hsl(var(--lime))] font-semibold">AFTER</span>
          <span className="font-num text-[13px] font-semibold text-[hsl(var(--foreground))]">{afterLabel}</span>
        </div>
        <div className={`${barH} rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden`}>
          <motion.div initial={{ width: 0 }} animate={{ width: inView ? `100%` : 0 }} transition={{ duration: 1.2, delay: 0.25, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full bg-[hsl(var(--lime))] shadow-[0_0_18px_rgba(212, 180, 106,0.4)]" />
        </div>
      </div>
    </div>
  );
};

// HERO CASE — full width, dramatic
const HeroCase = ({ c, idx, onCtaClick }) => (
  <motion.article
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -4 }}
    data-testid={`case-card-${idx}`}
    className="group relative rounded-[28px] overflow-hidden border border-[rgba(212, 180, 106,0.28)] bg-gradient-to-br from-[rgba(212, 180, 106,0.04)] via-[rgba(255,255,255,0.02)] to-[rgba(212, 180, 106,0.02)] shadow-[0_40px_120px_-30px_rgba(212, 180, 106,0.3)] hover:shadow-[0_50px_140px_-20px_rgba(212, 180, 106,0.45)] transition-[box-shadow,transform] duration-400"
  >
    {/* Featured badge */}
    <div className="absolute top-5 right-5 sm:top-6 sm:right-6 z-10">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#050505] bg-[hsl(var(--lime))] px-2.5 py-1 rounded-full">
        <Award className="w-3 h-3" />
        FEATURED
      </span>
    </div>

    {/* Glow blobs */}
    <div className="pointer-events-none absolute -top-32 -left-20 w-96 h-96 rounded-full opacity-50 group-hover:opacity-90 transition-opacity duration-500 blur-3xl" style={{ background: "radial-gradient(circle, rgba(212, 180, 106,0.4), transparent 70%)" }} aria-hidden />
    <div className="pointer-events-none absolute -bottom-32 -right-20 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, rgba(212, 180, 106,0.25), transparent 70%)" }} aria-hidden />

    <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-10 p-5 sm:p-10 lg:p-12">
      {/* Left: KPI hero */}
      <div className="lg:col-span-7">
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="font-num text-[11px] font-bold text-[hsl(var(--lime))] bg-[rgba(212, 180, 106,0.1)] border border-[rgba(212, 180, 106,0.35)] px-2 py-0.5 rounded">
            {String(idx + 1).padStart(2, "0")} / CASE
          </span>
          <span className="text-[13px] sm:text-[14px] text-[hsl(var(--foreground))]/80">{c.industry}</span>
          <span className="text-[12px] text-[hsl(var(--muted-foreground))]">· {c.period}</span>
        </div>

        <div className="mt-4 sm:mt-8">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--lime))] mb-2 sm:mb-3">
            <TrendingUp className="w-3 h-3" />
            關鍵成果
          </div>
          <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
            <span className="stat-num text-[58px] sm:text-[92px] lg:text-[116px] leading-[0.9] text-[hsl(var(--lime))] drop-shadow-[0_0_40px_rgba(212, 180, 106,0.45)]" data-testid={`case-kpi-${idx}`}>
              <CountUp value={c.kpiNumber} duration={1.8} />
            </span>
            <span className="font-serif-tc text-[18px] sm:text-2xl lg:text-[30px] text-[hsl(var(--foreground))]/85 font-medium">
              {c.kpiUnit}
            </span>
          </div>
          <p className="mt-3 sm:mt-4 text-[13px] sm:text-[17px] text-[hsl(var(--foreground))]/80 leading-relaxed max-w-[52ch]">
            {c.kpiCaption}
          </p>
        </div>

        {/* Inline pseudo-testimonial placeholder (subtle) */}
        <div className="mt-5 sm:mt-8 pt-4 sm:pt-5 border-t border-[rgba(255,255,255,0.08)] flex items-start gap-2.5 sm:gap-3">
          <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[hsl(var(--lime))] text-[#050505] font-serif-tc font-bold flex items-center justify-center text-[12px] sm:text-sm flex-shrink-0">{c.industry.charAt(0)}</div>
          <div className="text-[11px] sm:text-[13px] text-[hsl(var(--muted-foreground))] italic leading-relaxed">
            「3 個月從 0 到 15,000 粉絲，學生自動上門諮詢。」
            <span className="not-italic text-[hsl(var(--muted-foreground))]/70"> — 補習導師 K.</span>
          </div>
        </div>
      </div>

      {/* Right: Growth bar + CTA (CTA hidden on mobile, form CTA enough) */}
      <div className="lg:col-span-5 lg:pl-6 lg:border-l lg:border-[rgba(255,255,255,0.08)] pt-4 lg:pt-0 border-t border-[rgba(255,255,255,0.08)] lg:border-t-0">
        <GrowthBar
          size="lg"
          metricLabel={`${c.metricLabel} · ${c.growthLabel}`}
          beforeValue={c.beforeValue}
          afterValue={c.afterValue}
          beforeLabel={c.beforeLabel}
          afterLabel={c.afterLabel}
        />
        <div className="mt-8 hidden lg:block">
          <Button onClick={onCtaClick} className="btn-outline-soft h-11 px-5 text-[13px] w-full sm:w-auto">
            了解這個案例背後的系統
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  </motion.article>
);

// COMPACT CASE TILE
const CompactTile = ({ c, idx }) => (
  <motion.article
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.45, delay: (idx % 2) * 0.06 }}
    whileHover={{ y: -3 }}
    data-testid={`case-card-${idx}`}
    className="group relative rounded-[20px] overflow-hidden border border-[rgba(255,255,255,0.08)] bg-gradient-to-b from-[rgba(255,255,255,0.03)] to-[rgba(255,255,255,0.01)] hover:border-[rgba(212, 180, 106,0.4)] hover:shadow-[0_0_0_1px_rgba(212, 180, 106,0.18),0_24px_60px_-20px_rgba(212, 180, 106,0.22)] transition-[border-color,box-shadow,transform] duration-300 p-5 sm:p-6"
  >
    <div className="pointer-events-none absolute -top-16 -right-12 w-44 h-44 rounded-full opacity-30 group-hover:opacity-80 transition-opacity duration-500 blur-3xl" style={{ background: "radial-gradient(circle, rgba(212, 180, 106,0.3), transparent 70%)" }} aria-hidden />

    <div className="relative flex items-center justify-between min-w-0">
      <span className="font-num text-[10px] font-bold text-[hsl(var(--lime))] bg-[rgba(212, 180, 106,0.1)] border border-[rgba(212, 180, 106,0.3)] px-1.5 py-0.5 rounded">
        {String(idx + 1).padStart(2, "0")}
      </span>
      <span className="text-[11px] text-[hsl(var(--muted-foreground))] flex-shrink-0">{c.period}</span>
    </div>

    <div className="relative mt-3 text-[12px] sm:text-[13px] text-[hsl(var(--foreground))]/80 truncate font-medium">
      {c.industry}
    </div>

    <div className="relative mt-4 flex items-baseline gap-1.5 flex-wrap">
      <span className="stat-num text-[30px] sm:text-[38px] leading-none text-[hsl(var(--lime))] drop-shadow-[0_0_18px_rgba(212, 180, 106,0.35)]" data-testid={`case-kpi-${idx}`}>
        <CountUp value={c.kpiNumber} duration={1.5} />
      </span>
      <span className="font-serif-tc text-[12px] sm:text-[13px] text-[hsl(var(--foreground))]/80">
        {c.kpiUnit}
      </span>
    </div>

    <p className="relative mt-2 text-[12px] sm:text-[13px] text-[hsl(var(--muted-foreground))] leading-snug line-clamp-2">
      {c.kpiCaption}
    </p>

    {/* Mini bar */}
    <div className="relative mt-4 flex items-center gap-2">
      <Sparkles className="w-3 h-3 text-[hsl(var(--lime))] flex-shrink-0" />
      <span className="font-num text-[11px] text-[hsl(var(--foreground))]/85">{c.growthLabel}</span>
      <span className="text-[10px] text-[hsl(var(--muted-foreground))]/70">{c.beforeLabel} → {c.afterLabel}</span>
    </div>
  </motion.article>
);

export const StudentCases = ({ onCtaClick }) => {
  const [hero, ...rest] = CASES;

  return (
    <section
      id="cases"
      data-testid="student-cases-section"
      className="relative bg-ink-soft py-14 sm:py-24 lg:py-28"
    >
      <div className="absolute top-0 left-0 right-0 hairline-h" aria-hidden />
      <div className="container px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <motion.div {...SCROLL_REVEAL} className="max-w-3xl">
          <Eyebrow label="RESULTS" sub="05" />
          <h2 className="font-serif-tc mt-5 sm:mt-7 text-[30px] leading-[1.15] sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em]">
            5 個行業，<span className="lime-keyword">同一套方法</span>
          </h2>
          <p className="mt-4 sm:mt-6 text-[14px] sm:text-base leading-relaxed text-[hsl(var(--muted-foreground))] max-w-[58ch]">
            真實操作數據。每個案例都使用同一套 AI × 定位 × 內容 × 變現系統。
          </p>
        </motion.div>

        {/* Hero case */}
        <div className="mt-10 sm:mt-14">
          <HeroCase c={hero} idx={0} onCtaClick={onCtaClick} />
        </div>

        {/* 4 compact tiles */}
        <div className="mt-5 sm:mt-6 grid grid-cols-2 gap-3 sm:gap-5 lg:gap-6">
          {rest.map((c, i) => (
            <CompactTile key={c.id} c={c} idx={i + 1} />
          ))}
        </div>

        <motion.div {...SCROLL_REVEAL} className="mt-10 sm:mt-12 flex flex-col items-center gap-3">
          <p className="text-[13px] sm:text-sm text-[hsl(var(--muted-foreground))] text-center max-w-xl">
            分享會將現場拆解這些案例的核心邏輯 — 為何能做到、你如何複製。
          </p>
          <Button
            data-testid="webinar-cta-cases-button"
            onClick={onCtaClick}
            size="lg"
            className="btn-lime w-full sm:w-auto h-12 px-6 text-[15px]"
          >
            {WEBINAR.ctaCopy}
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
